import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import ReactApexChart from 'react-apexcharts';
import { Grid, BarChart2, Calendar } from 'react-feather';
import StethoscopeLoading from '../../../components/StethoscopeLoading/StethoscopeLoading';
import { format, subMonths, parseISO } from 'date-fns';
import { useDispatch } from 'react-redux';
import { DateRangeSelector } from './dateRangerPicker';
import './viewTrends.css';
import moment from 'moment';
import { Carousel } from 'react-bootstrap';
import { trendsGet } from "apiServices/reducer/apiService";
import { mrSitesReportsGetByReportID } from 'apiServices/reducer/apiService';
import { useSelector } from 'react-redux';


// Mock data simulating the table in the screenshot


const ViewTrends = (props) => {
    // State management
    const [loading, setLoading] = useState(false);
    const [viewType, setViewType] = useState('Table');
    const [visibleRange, setVisibleRange] = useState(true);
    const [sortColumn, setSortColumn] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [currentChartIndex, setCurrentChartIndex] = useState(0);
    const [filterText, setFilterText] = useState('');
    const [selectedRangeValue, setSelectedRangeValue] = useState(3);
    const [customDates, setCustomDates] = useState(false);
    const [dates, setDates] = useState({
        startDate: null,
        endDate: null
    });
    const dispatch = useDispatch();
    const [hematologyData, setHematologyData] = useState([]);
    const [filteredHematologyData, setFilteredHematologyData] = useState([]);
    const [chartDataArray, setChartDataArray] = useState([]);
    const [trendLabel, setTrendLabel] = useState('');
    const [dateKeys, setDateKeys] = useState([]);
    const [apiData, setApiData] = useState([]);
    const [reportsToCompare, setReportsToCompare] = useState(props.reportsToCompare || []);
    const [compareReportOne, setCompareReportOne] = useState({ blobUrl: null, name: '' });
    const [compareReportTwo, setCompareReportTwo] = useState({ blobUrl: null, name: '' });
    const [compareReportLoading, setCompareReportLoading] = useState({ one: false, two: false });

    console.log(props, "props at Compare")

    // Optimized function for trendsGet API calls


    const fetchTrendsData = (startDate, endDate, successCallback = null) => {
        const params = {
            user_id: props.user_id,
            token: props.token,
            startDate: format(startDate, 'yyyy-MM-dd'),
            endDate: format(endDate, 'yyyy-MM-dd')
        };

        // Set loading state
        setLoading(true);

        // Only clear states that need to be cleared before new data
        setApiData([]);
        setHematologyData([]);
        setFilteredHematologyData([]);
        setDateKeys([]);
        setChartDataArray([]);
        setTrendLabel(''); // Clear label instead of setting error message

        return dispatch(trendsGet(params))
            .unwrap()
            .then((response) => {
                console.log("trendsData", response);
                const data = response || [];
                setApiData(data);

                // Call success callback if provided, otherwise process data directly
                if (successCallback) {
                    successCallback(startDate, endDate);
                } else {
                    // Process the data directly here instead of calling handleSearch
                    processTrendsData(data, startDate, endDate);
                }

                return response;
            })
            .catch((error) => {
                console.error("Error fetching trends data:", error);
                // Clear all states on error
                setApiData([]);
                setHematologyData([]);
                setFilteredHematologyData([]);
                setDateKeys([]);
                setTrendLabel('Error loading data');
                setChartDataArray([]);
                setDates({
                    startDate: null,
                    endDate: null
                });
                throw error;
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const fetchCompareReportData = () => {
        console.log(reportsToCompare, "reportsToCompare")

        if (!reportsToCompare || reportsToCompare.length === 0) {
            console.log("No reports to compare");
            setCompareReportLoading({ one: false, two: false });
            return;
        }

        // Clear previous reports
        setCompareReportOne({ blobUrl: null, name: '' });
        setCompareReportTwo({ blobUrl: null, name: '' });
        setCompareReportLoading({
            one: reportsToCompare.length > 0,
            two: reportsToCompare.length > 1
        });

        // Process each checked report
        reportsToCompare.slice(0, 2).forEach((report, index) => {
            console.log(`Processing report ${index + 1}:`, report.reportId);
            const loadingKey = index === 0 ? 'one' : 'two';

            const compareParams = {
                ReportID: report.reportId,
                UserID: props.user_id,
                token: props.token
            };

            console.log("Compare params for report:", report.reportId, compareParams);

            dispatch(mrSitesReportsGetByReportID(compareParams))
                .then((response) => {
                    console.log(`Report ${index + 1} data:`, response);

                    // Create blob URL from response
                    const blob = new Blob([response.payload], { type: 'application/pdf' });
                    const blobUrl = URL.createObjectURL(blob);
                    console.log(`Created blob URL for report ${index + 1}:`, blobUrl);

                    // Set the blob URL to the appropriate state based on index
                    if (index === 0) {
                        setCompareReportOne({ blobUrl, name: report.name });
                    } else if (index === 1) {
                        setCompareReportTwo({ blobUrl, name: report.name });
                    }
                })
                .catch(error => {
                    console.error(`Error fetching report ${index + 1}:`, error);
                    if (index === 0) {
                        setCompareReportOne({ blobUrl: null, name: report.name });
                    } else if (index === 1) {
                        setCompareReportTwo({ blobUrl: null, name: report.name });
                    }
                })
                .finally(() => {
                    setCompareReportLoading(prev => ({
                        ...prev,
                        [loadingKey]: false
                    }));
                });
        });
    }

    // New function to process trends data
    const processTrendsData = (data, startDate, endDate) => {
        if (!data || data.length === 0) {
            setHematologyData([]);
            setFilteredHematologyData([]);
            setDateKeys([]);
            setTrendLabel('No data available');
            setChartDataArray([]);
            return;
        }

        // Extract all unique dates from the data and ensure they are valid
        const allDates = new Set();
        data.forEach(item => {
            Object.keys(item).forEach(key => {
                // Check if the key is a date (matches MM/DD/YYYY format)
                if (/^\d{2}\/\d{2}\/\d{4}$/.test(key)) {
                    const dateParts = key.split('/');
                    const date = new Date(
                        parseInt(dateParts[2]), // year
                        parseInt(dateParts[0]) - 1, // month (0-based)
                        parseInt(dateParts[1]) // day
                    );
                    if (!isNaN(date.getTime())) {
                        allDates.add(key);
                    }
                }
            });
        });

        // Convert to array and sort dates
        const sortedDates = Array.from(allDates).sort((a, b) => {
            const dateA = new Date(a.split('/').join('-'));
            const dateB = new Date(b.split('/').join('-'));
            return dateA - dateB;
        });

        setHematologyData(data);
        setFilteredHematologyData(data);
        setDateKeys(sortedDates);
        setTrendLabel(`Trends showing from <span class="date-end">${format(startDate, 'MM-dd-yyyy')}</span> to <span class="date-end">${format(endDate, 'MM-dd-yyyy')}</span>`);

        // Pass the sortedDates to prepareChartData
        prepareChartData(data, sortedDates);
    };

    // Duration options matching Angular version
    // Initialize with 1 year data (default selection)
    useEffect(() => {
        const endDate = new Date();
        const startDate = subMonths(endDate, 12); // 1 year = 12 months
        setDates({ startDate, endDate });

        if (props.isCompare) {
            fetchCompareReportData()
        } else {
            fetchTrendsData(startDate, endDate);
        }

    }, []);

    const handleRangeChange = (range, value) => {
        console.log("range", range, "value", value);

        // Calculate dates before clearing states
        const now = new Date();
        let startDate, endDate;

        if (value === 'custom') {
            setCustomDates(true);
            // For custom, don't make API call immediately - let user select dates
            return;
        } else if (range === 'month') {
            setCustomDates(false);
            let monthsToSubtract;

            // Handle different value formats
            if (typeof value === 'string') {
                if (value.includes('y')) {
                    const yearValue = parseInt(value.split('y')[0]);
                    monthsToSubtract = yearValue * 12;
                } else if (value.includes('m')) {
                    monthsToSubtract = parseInt(value.split('m')[0]);
                } else {
                    // Handle numeric values
                    monthsToSubtract = parseInt(value) || 3;
                }
            } else {
                // Handle numeric values directly
                monthsToSubtract = value || 3;
            }

            console.log("monthsToSubtract", monthsToSubtract);
            startDate = subMonths(now, monthsToSubtract);
            endDate = now;

            // Validate the dates
            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                console.error('Invalid date calculated:', { startDate, endDate, monthsToSubtract });
                return;
            }
        } else {
            // Default to 3 months if range is not recognized
            setCustomDates(false);
            startDate = subMonths(now, 3);
            endDate = now;
        }

        // Update dates state with new values
        setDates({
            startDate: startDate,
            endDate: endDate
        });

        // Make API call - fetchTrendsData will handle state clearing
        fetchTrendsData(startDate, endDate);
    };



    const handleSearch = async (startDate = dates.startDate, endDate = dates.endDate) => {
        if (!startDate || !endDate) {
            console.error('Invalid dates provided:', { startDate, endDate });
            return;
        }

        // Ensure we have valid Date objects
        try {
            startDate = new Date(startDate);
            endDate = new Date(endDate);

            // Validate the dates
            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                console.error('Invalid date objects created:', { startDate, endDate });
                return;
            }

            if (customDates && startDate >= endDate) {
                alert('End date should be after start date.');
                return;
            }

            setLoading(true);
            try {
                // Use apiData instead of props.data
                if (!apiData || apiData.length === 0) {
                    setHematologyData([]);
                    setFilteredHematologyData([]);
                    setDateKeys([]);
                    setTrendLabel('No data available');
                    setChartDataArray([]);
                    return;
                }

                // Process the data using the new function
                processTrendsData(apiData, startDate, endDate);

            } catch (error) {
                console.error('Error processing trends data:', error);
            }
        } catch (error) {
            console.error('Error handling dates:', error);
        } finally {
            setLoading(false);
        }
    };

    const prepareChartData = (data, dateKeysToUse) => {
        // Use the passed dateKeys or fall back to the state
        console.log(dateKeysToUse, "dateKeysToUse at prepareChartData")
        const datesToUse = dateKeysToUse || dateKeys;

        if (!datesToUse || datesToUse.length === 0) {
            console.log('No date keys available for chart data');
            setChartDataArray([]);
            return;
        }
        console.log(data, "data at prepareChartData")

        const newChartDataArray = data.map((item) => {
            // Create data points from actual dates and values
            const seriesData = datesToUse.map(date => {
                const value = item[date];
                if (value === null || value === undefined) return null;

                const isInRange = value >= parseFloat(item.min_range) && value <= parseFloat(item.max_range);
                return {
                    x: date,
                    y: parseFloat(value),
                    fillColor: isInRange ? '#00E396' : '#FF4560',
                    strokeColor: isInRange ? '#00B36B' : '#D32F2F',
                };
            }).filter(point => point !== null);

            const rangeUnit = item.range.split(' ')[1] || 'Value';
            console.log(seriesData, "seriesData at prepareChartData")

            return {
                series: [{
                    name: item.parameter_name,
                    data: seriesData
                }],
                chart: {
                    type: 'line',
                    height: 350,
                    animations: {
                        enabled: true,
                        easing: 'linear',
                        dynamicAnimation: {
                            speed: 1000
                        }
                    },
                    toolbar: {
                        show: false,
                        offsetX: -9999,
                        offsetY: -9999,
                        tools: {
                            download: false,
                            selection: false,
                            zoom: false,
                            zoomin: false,
                            zoomout: false,
                            pan: false,
                            reset: false,
                            customIcons: []
                        },
                    },
                    zoom: {
                        enabled: true,
                        type: 'x',
                        autoScaleYaxis: true
                    },
                    selection: {
                        enabled: false,
                        xaxis: {
                            min: undefined,
                            max: undefined
                        },
                        yaxis: {
                            min: undefined,
                            max: undefined
                        }
                    },
                    dropShadow: {
                        enabled: true,
                        top: 10,
                        left: 0,
                        blur: 4,
                        color: '#000000',
                        opacity: 0.15
                    },
                },
                annotations: {
                    yaxis: [{
                        y: parseFloat(item.min_range),
                        borderColor: '#00E396',
                        label: {
                            borderColor: '#00E396',
                            style: {
                                color: '#fff',
                                background: '#00E396',
                            },
                            text: 'Min Range',
                        }
                    }, {
                        y: parseFloat(item.max_range),
                        borderColor: '#FF4560',
                        label: {
                            borderColor: '#FF4560',
                            style: {
                                color: '#fff',
                                background: '#FF4560',
                            },
                            text: 'Max Range',
                        }
                    }]
                },
                markers: {
                    size: 5,
                    colors: undefined,
                    strokeColors: '#fff',
                    strokeWidth: 2,
                    strokeOpacity: 0.9,
                    strokeDashArray: 0,
                    fillOpacity: 1,
                    discrete: [],
                    shape: "circle",
                    radius: 2,
                    offsetX: 0,
                    offsetY: 0,
                    onClick: undefined,
                    onDblClick: undefined,
                    showNullDataPoints: true,
                    hover: {
                        size: 7,
                        sizeOffset: 3
                    }
                },
                stroke: {
                    curve: 'smooth',
                    width: 3,
                },
                fill: {
                    type: 'gradient',
                    gradient: {
                        shade: 'dark',
                        gradientToColors: ['#FDD835'],
                        shadeIntensity: 1,
                        type: 'horizontal',
                        opacityFrom: 1,
                        opacityTo: 1,
                        stops: [0, 100, 100, 100]
                    },
                },
                xaxis: {
                    type: 'datetime',
                    labels: {
                        datetimeUTC: false,
                        format: 'MM/dd/yyyy',
                        rotate: -45,
                        style: {
                            fontSize: '18px',
                            colors: '#8e8da4'
                        }
                    },
                    title: {
                        text: rangeUnit,
                        style: {
                            color: '#8e8da4'
                        }
                    },
                    tooltip: {
                        enabled: false
                    }
                },
                yaxis: {
                    title: {
                        text: rangeUnit,
                        style: {
                            color: '#8e8da4'
                        }
                    },
                    labels: {
                        formatter: function (value) {
                            return value.toFixed(2);
                        },
                        style: {
                            colors: '#8e8da4'
                        }
                    }
                },
                title: {
                    text: item.parameter_name,
                    align: 'center',
                    style: {
                        fontSize: '18px',
                        fontWeight: '500',
                        color: '#333'
                    }
                },
                legend: {
                    show: false
                },
                tooltip: {
                    enabled: true,
                    shared: false,
                    y: {
                        formatter: function (val) {
                            return val + " " + rangeUnit
                        }
                    },
                    x: {
                        format: 'dd/MM/yy HH:mm',
                        style: {
                            color: '#8e8da4'
                        }
                    },
                    marker: {
                        show: true,
                    },
                },
                grid: {
                    borderColor: '#f1f1f1',
                    strokeDashArray: 0,
                }
            };
        });

        console.log('Setting chart data array:', newChartDataArray);
        setChartDataArray(newChartDataArray);
    };

    const handleViewChange = (type) => {
        setViewType(type);
        if (type === 'Chart') {
            setCurrentChartIndex(0);
        } else if (type === 'Table') {
            setFilterText('');
            setFilteredHematologyData(hematologyData);
        }
    };

    const handleFilterChange = (value) => {
        if (!value) {
            setFilteredHematologyData(hematologyData);
        } else {
            const filtered = hematologyData.filter(item =>
                item.parameter_name.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredHematologyData(filtered);
        }
        setFilterText(value);
    };

    const handleSort = (column) => {
        // Calculate the new sort direction first
        let newDirection;
        if (sortColumn === column) {
            newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            newDirection = 'asc';
        }

        // Update both states together
        setSortColumn(column);
        setSortDirection(newDirection);

        // Use the calculated direction for sorting
        const sortedData = [...filteredHematologyData].sort((a, b) => {
            const valueA = a[column];
            const valueB = b[column];
            const modifier = newDirection === 'asc' ? 1 : -1;
            return valueA < valueB ? -modifier : valueA > valueB ? modifier : 0;
        });

        setFilteredHematologyData(sortedData);
    };

    const handleChartNavigation = (direction) => {
        if (direction === 'prev' && currentChartIndex > 0) {
            setCurrentChartIndex(currentChartIndex - 1);
        } else if (direction === 'next' && currentChartIndex < chartDataArray.length - 1) {
            setCurrentChartIndex(currentChartIndex + 1);
        }
    };

    // Utility functions
    const generateTrendPoints = (item) => {
        const points = dateKeys.map((date, index) => {
            const value = item[date];
            if (value === null || value === undefined) return null;

            const x = 100 + (index * (150 / (dateKeys.length - 1)));
            const minY = Math.min(...dateKeys.map(d => item[d]).filter(v => v !== null));
            const maxY = Math.max(...dateKeys.map(d => item[d]).filter(v => v !== null));
            const range = maxY - minY;

            const y = 35 - ((value - minY) / range) * 30;
            return { x, y };
        });

        return points.filter(point => point !== null);
    };

    const generateTrendSegments = (item) => {
        const points = generateTrendPoints(item);
        const segments = [];

        for (let i = 0; i < points.length - 1; i++) {
            const previousInRange = isInRange(item, dateKeys[i]);
            const currentInRange = isInRange(item, dateKeys[i + 1]);
            let color = 'grey';

            if (previousInRange && currentInRange) {
                color = 'green';
            } else if (!previousInRange && currentInRange) {
                color = 'green';
            } else if (previousInRange && !currentInRange) {
                color = 'red';
            } else if (!previousInRange && !currentInRange) {
                color = 'red';
            }

            segments.push({
                x1: points[i].x,
                y1: points[i].y,
                x2: points[i + 1].x,
                y2: points[i + 1].y,
                color: color
            });
        }

        return segments;
    };

    const isInRange = (item, date) => {
        const value = item[date];
        return value !== null && value !== undefined && value >= item.min_range && value <= item.max_range;
    };

    const openChart = (parameterId) => {
        // chartDataArray follows hematologyData order, not the filtered table order
        const index = hematologyData.findIndex(
            (data) => String(data.parameterID) === String(parameterId)
        );
        if (index !== -1) {
            setViewType('Chart');
            setCurrentChartIndex(index);
        }
    };

    // Add a new function to handle custom date selection
    const handleCustomDateChange = (dateInfo) => {
        console.log("Custom date change received:", dateInfo);
        const { startDate, endDate, isStartDate, range } = dateInfo;

        // Update dates state
        setDates(prev => ({
            ...prev,
            startDate: isStartDate ? startDate : prev.startDate,
            endDate: !isStartDate ? endDate : prev.endDate
        }));

        // If both dates are selected, make API call
        if (startDate && endDate) {
            // Format dates for API call
            const formattedStartDate = format(startDate, 'yyyy-MM-dd');
            const formattedEndDate = format(endDate, 'yyyy-MM-dd');

            // Make API call
            fetchTrendsData(startDate, endDate);
        }
    };

    // Add new function to handle start date changes
    const handleCustomDateChangeforStart = (dateInfo) => {
        console.log("Start date change received:", dateInfo);
        const { startDate, endDate } = dateInfo;

        // Update dates state with new start date
        setDates(prev => ({
            ...prev,
            startDate: startDate,
            endDate: prev.endDate // Keep existing end date
        }));

        // If both dates are selected, make API call
        if (startDate && dates.endDate) {
            // Format dates for API call
            const formattedStartDate = format(startDate, 'yyyy-MM-dd');
            const formattedEndDate = format(dates.endDate, 'yyyy-MM-dd');

            // Make API call
            // fetchTrendsData(startDate, dates.endDate);
        }
    };

    // Add new function to handle end date changes
    const handleCustomDateChangeforEnd = (dateInfo) => {
        console.log("End date change received:", dateInfo);
        const { startDate, endDate } = dateInfo;

        // Update dates state with new end date
        setDates(prev => ({
            ...prev,
            startDate: prev.startDate, // Keep existing start date
            endDate: endDate
        }));

        // If both dates are selected, make API call
        if (dates.startDate && endDate) {
            // Format dates for API call
            const formattedStartDate = format(dates.startDate, 'yyyy-MM-dd');
            const formattedEndDate = format(endDate, 'yyyy-MM-dd');

            // Make API call
            // fetchTrendsData(dates.startDate, endDate);
        }
    };

    const handleDateChange = (dateInfo) => {
        console.log("Date change received:", dateInfo);
        const { startDate, endDate, range } = dateInfo;

        if (startDate && endDate) {
            setDates({
                startDate,
                endDate
            });

            // Format dates for API call
            const formattedStartDate = format(startDate, 'yyyy-MM-dd');
            const formattedEndDate = format(endDate, 'yyyy-MM-dd');

            // Make API call
            fetchTrendsData(startDate, endDate);
        }
    };

    // Render functions
    const renderLoadingOverlay = () => (
        loading && (
            <div className="loading-overlay">
                <StethoscopeLoading message="Loading..." />
            </div>
        )
    );

    const renderComparePreviewLoader = (message) => (
        <StethoscopeLoading className="h-100" message={message} />
    );



    const renderTableView = () => {
        return (
            <div className="table-view-wrapper">
                <div className="d-flex justify-content-between align-items-center mt-1 table-controls">
                    <div className="filter-container">
                        <input
                            type="text"
                            placeholder="Filter by Hematology Name"
                            value={filterText}
                            onChange={(e) => handleFilterChange(e.target.value)}
                            className="form-control"
                        />
                    </div>
                    <div className="form-check form-switch">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="rangeToggle"
                            checked={visibleRange}
                            onChange={(e) => setVisibleRange(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="rangeToggle">Range</label>
                    </div>
                </div>
                <div className="trends-table-responsive">
                    <table className="trends-table">
                        <thead>
                            <tr>
                                <th
                                    className="table-cell sticky-header hematology-name-header"
                                    onClick={() => handleSort('parameter_name')}
                                >
                                    Hematology Name
                                    {sortColumn === 'parameter_name' && (
                                        <span className="sort-icon" style={{ marginLeft: 8 }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                                    )}
                                </th>
                                {visibleRange && (
                                    <th className="table-cell sticky-header range-header">
                                        Range
                                    </th>
                                )}
                                {dateKeys.length > 1 && (
                                    <th className={`table-cell sticky-header trend-header ${!visibleRange ? 'no-range' : ''}`}>
                                        Trend
                                    </th>
                                )}
                                {dateKeys.map(date => (
                                    <th key={date} className="table-cell date-header">{date}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredHematologyData.map((item, i) => (
                                <tr key={item.parameterID} className={i % 2 === 0 ? 'even-row' : 'odd-row'}>
                                    <td className="table-cell sticky-cell hematology-name-cell">
                                        {item.parameter_name}
                                    </td>
                                    {visibleRange && (
                                        <td className="table-cell sticky-cell range-cell">
                                            {item.range}
                                        </td>
                                    )}
                                    {dateKeys.length > 1 && (
                                        <td
                                            className={`table-cell sticky-cell trend-cell ${!visibleRange ? 'no-range' : ''}`}
                                            onClick={() => item.parameter_name !== 'Others' && openChart(item.parameterID)}
                                        >
                                            {item.parameter_name !== 'Others' ? (
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', cursor: 'pointer' }}>
                                                    {generateTrendPoints(item).length > 0 ? (
                                                        <svg width="100%" height="20" viewBox="100 -15 150 50" style={{ overflow: 'auto' }}>
                                                            {generateTrendSegments(item).map((segment, idx) => (
                                                                <line
                                                                    key={idx}
                                                                    x1={segment.x1}
                                                                    y1={segment.y1}
                                                                    x2={segment.x2}
                                                                    y2={segment.y2}
                                                                    stroke={segment.color}
                                                                    strokeWidth="2"
                                                                />
                                                            ))}
                                                            {generateTrendPoints(item).map((point, idx) => (
                                                                <circle
                                                                    key={idx}
                                                                    cx={point.x}
                                                                    cy={point.y}
                                                                    r="5"
                                                                    fill={isInRange(item, dateKeys[idx]) ? 'green' : 'red'}
                                                                    stroke={isInRange(item, dateKeys[idx]) ? 'darkgreen' : 'darkred'}
                                                                    strokeWidth="1"
                                                                />
                                                            ))}
                                                        </svg>
                                                    ) : (
                                                        <span>N/A</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span>N/A</span>
                                            )}
                                        </td>
                                    )}
                                    {dateKeys.map(date => (
                                        <td key={date} className="table-cell value-cell">
                                            {item[date] !== null ? (
                                                item.parameter_name !== 'Others' ? (
                                                    <span className={
                                                        item[date] >= item.min_range && item[date] <= item.max_range
                                                            ? 'badge-success'
                                                            : 'badge-danger'
                                                    }>
                                                        {item[date]}
                                                    </span>
                                                ) : (
                                                    <span>{item[date]}</span>
                                                )
                                            ) : (
                                                <span>-</span>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            {apiData.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={visibleRange ? dateKeys.length + 3 : dateKeys.length + 2}
                                        className="table-cell"
                                        style={{ textAlign: 'center' }}
                                    >
                                        No data found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const handleChartWheel = (e) => {
        const container = e.currentTarget;
        const { scrollTop, scrollHeight, clientHeight } = container;
        const isScrollingUp = e.deltaY < 0;
        const isScrollingDown = e.deltaY > 0;
        
        // Check if we can scroll in the requested direction
        const canScrollUp = scrollTop > 0;
        const canScrollDown = scrollTop < scrollHeight - clientHeight;
        
        // If we can scroll, prevent default and scroll manually
        if ((isScrollingUp && canScrollUp) || (isScrollingDown && canScrollDown)) {
            e.preventDefault();
            container.scrollTop += e.deltaY;
        }
    };

    const renderChartView = () => (
        <div 
            className="chart-view-container"
            onWheel={handleChartWheel}
        >
            <div className="chart-card">
                <div className="chart-wrapper">
                    <button
                        className={`chart-navigation-button prev ${currentChartIndex === 0 ? 'disabled' : ''}`}
                        onClick={() => handleChartNavigation('prev')}
                        disabled={currentChartIndex === 0}
                    >
                        ‹
                    </button>

                    {console.log('Chart rendering - chartDataArray length:', chartDataArray, 'currentIndex:', currentChartIndex)}
                    {chartDataArray.length > 0 && chartDataArray[currentChartIndex] ? (
                        <ReactApexChart
                            options={(() => {
                                const { series: _series, ...chartOptions } = chartDataArray[currentChartIndex];
                                return chartOptions;
                            })()}
                            series={chartDataArray[currentChartIndex].series}
                            type="area"
                            height={400}
                        />
                    ) : (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                            <p>No chart data available</p>
                        </div>
                    )}

                    <button
                        className={`chart-navigation-button next ${currentChartIndex === chartDataArray.length - 1 ? 'disabled' : ''}`}
                        onClick={() => handleChartNavigation('next')}
                        disabled={currentChartIndex === chartDataArray.length - 1}
                    >
                        ›
                    </button>
                </div>
            </div>
            <div className="chart-pagination-pills">
                {chartDataArray.map((chart, index) => (
                    <button
                        key={index}
                        className={`pagination-pill ${index === currentChartIndex ? 'active' : ''}`}
                        onClick={() => setCurrentChartIndex(index)}
                    >
                        {chart.series[0].name}
                    </button>
                ))}
            </div>
        </div>
    );


    return (
        <>
            <Modal
                show={props.show}
                onHide={props.onHide}
                size="xl"
                dialogClassName="trends-modal trends-modal-large"
                contentClassName="trends-modal-content"
                backdrop="static"
                keyboard={false}
                centered
                enforceFocus={false}
            >
                <Modal.Header closeButton={false}>
                    <Modal.Title>{props.isCompare ? "Compare Reports" : "Trends"}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {renderLoadingOverlay()}
                    {props.isCompare ? (
                        <div className="row vh-100">
                            <div className="col-md-6 d-flex flex-column">
                                <h5 className="text-center p-2 text-primary">{compareReportOne.name}</h5>
                                {compareReportLoading.one ? (
                                    renderComparePreviewLoader("Loading report preview...")
                                ) : compareReportOne.blobUrl ? (
                                    <iframe src={compareReportOne.blobUrl} width="100%" height="100%" title="Report 1"></iframe>
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center h-100">
                                        <p>Loading...</p>
                                    </div>
                                )}
                            </div>
                            <div className="col-md-6 d-flex flex-column">
                                <h5 className="text-center p-2 text-primary">{compareReportTwo.name}</h5>
                                {compareReportLoading.two ? (
                                    renderComparePreviewLoader("Loading report preview...")
                                ) : compareReportTwo.blobUrl ? (
                                    <iframe src={compareReportTwo.blobUrl} width="100%" height="100%" title="Report 2"></iframe>
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center h-100">
                                        <p>Loading...</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="trends-body">
                            <div className="trends-fixed-header">
                                <DateRangeSelector
                                    onRangeChange={handleRangeChange}
                                    onCustomDateChangeforStart={handleCustomDateChangeforStart}
                                    onCustomDateChangeforEnd={handleCustomDateChangeforEnd}
                                    onSearch={(dateInfo) => {
                                        const { startDate, endDate } = dateInfo;
                                        if (startDate && endDate) {
                                            fetchTrendsData(startDate, endDate);
                                        }
                                    }}
                                />
                                {apiData.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center trends-showing-row">
                                        <div dangerouslySetInnerHTML={{ __html: trendLabel }} />
                                        <div className="d-flex px-4 py-2" role="group" aria-label="Change View">
                                            <OverlayTrigger
                                                placement="top"
                                                delay={{ show: 250, hide: 400 }}
                                                overlay={<Tooltip id="tooltip-table">Table</Tooltip>}
                                            >
                                                <Button
                                                    variant={viewType === 'Table' ? 'primary' : 'outline-primary'}
                                                    onClick={() => handleViewChange('Table')}
                                                    className={`me-2 view-change-btn ${viewType === 'Table' ? 'active-view' : ''}`}
                                                    aria-pressed={viewType === 'Table'}
                                                >
                                                    <Grid size={16} />
                                                </Button>
                                            </OverlayTrigger>
                                            <OverlayTrigger
                                                placement="top"
                                                delay={{ show: 250, hide: 400 }}
                                                overlay={<Tooltip id="tooltip-chart">Chart</Tooltip>}
                                            >
                                                <Button
                                                    variant={viewType === 'Chart' ? 'primary' : 'outline-primary'}
                                                    onClick={() => handleViewChange('Chart')}
                                                    className={`view-change-btn ${viewType === 'Chart' ? 'active-view' : ''}`}
                                                    aria-pressed={viewType === 'Chart'}
                                                >
                                                    <BarChart2 size={16} />
                                                </Button>
                                            </OverlayTrigger>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className={`trends-scrollable-content view-type-${viewType.toLowerCase()}`}>
                                {apiData.length === 0 ? (
                                    <h5 className="date-range-header" style={{ textAlign: 'center' }}>
                                        No data found on the selected dates
                                    </h5>
                                ) : apiData.length > 0 && (
                                    <>
                                        {viewType === 'Table' ? renderTableView() : renderChartView()}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="danger" onClick={props.onHide}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ViewTrends;